// IndexedDB wrapper z 4 object stores:
//   api_cache         key=path (string) → { data, cachedAt, expiresAt }
//   photo_queue       keyPath=id (uuid) → { id, addressId, floor, subfolder, slotType,
//                     bytes (ArrayBuffer, R2b-a) | blob (legacy), blobType?, blobSize?,
//                     fileName, createdAt, retryCount, lastRetryAt, status, lastError? }
//   building_configs  key=addressId     → { installType, floors, weIst, savedAt }
//   lost_photos       keyPath=id        → rejestr „Utracone" (R2b-b): metadane wpisów
//                     kolejki, których treść zniszczył system (martwe bloby iOS);
//                     zero binariów — tylko ślad dla technika/diagnostyki.
//   settings          key=string        → ustawienia PER URZĄDZENIE (V3 B9: `local_mode`);
//                     przeżywają restart i wylogowanie (clearAll ich NIE rusza).
//   upload_log        keyPath=id        → rejestr prób wysyłki (V3 B6): rozmiar przed/po
//                     downscale, czas, próby, typ sieci; ring ~200 wpisów, zero binariów.
//
// V3 słaby zasięg B-1 (12.09.2026): wpis kolejki dostaje opcjonalne `thumbBytes`
// (ArrayBuffer miniatury 160 px do ⏳ w slocie) i `meta` (downscale: bajty przed/po,
// wymiary, przejścia). Stare wpisy bez tych pól działają bez zmian (B8: migracja =
// dopisanie store'ów przy upgrade v2→v3, wpisy pending/failed NIETKNIĘTE).
//
// Backing abstraction: w browserze używa prawdziwego IndexedDB, w testach (Node)
// fallback do in-memory Map. Identyczna API.
//
// Public API: getCachedResponse, getCachedResponseEntry, setCachedResponse,
// invalidateCachedPrefix, addToPhotoQueue, getPhotoQueue, updateQueueItem,
// removeFromQueue, getQueueCount, getQueueStats, getCachedConfig, setCachedConfig,
// addToLostRegistry, getLostRegistry, getSetting, setSetting, addUploadLogEntry,
// getUploadLog.

// Plan posesji B2: jedna pisownia sekcji 0 w kluczu lokalizacji wpisu kolejki.
import { queueAddressId } from '../shared/planPosesji.js';

const DB_NAME = 'fiberops-offline';
// v2 (R2b, monit 1215 19.08): + store lost_photos.
// v3 (V3 słaby zasięg B-1, 12.09): + store settings (Tryb lokalny) + upload_log (B6).
const DB_VERSION = 3;
const UPLOAD_LOG_MAX = 200;
const DEFAULT_CACHE_TTL_MS = 5 * 60 * 1000;

// ─── Backing abstraction ─────────────────────────────────────────────────────

let _backing = null;

function getBacking() {
  if (_backing) return _backing;
  if (typeof globalThis !== 'undefined' && typeof globalThis.indexedDB !== 'undefined') {
    _backing = createIDBBacking();
  } else {
    // Fallback dla Node / SSR / testów bez fake-indexeddb
    _backing = createMemoryBacking();
  }
  return _backing;
}

/** Test hook — forsuje in-memory backing. */
export function __useMockBacking() {
  _backing = createMemoryBacking();
  invalidateQueueStats();
  return _backing;
}

/** Test hook — reset (następne getBacking() zdecyduje o type). */
export function __resetBacking() {
  _backing = null;
  invalidateQueueStats();
}

// UZUPEŁNIENIE 2 (12.09): getQueueStats() czyta CAŁE wpisy kolejki (IDB nie ma projekcji, więc
// każde wywołanie materializuje N × ~0,7-2,3 MB bajtów zdjęć). Na jedno zdarzenie kolejki pyta
// kilka hooków useOffline naraz (baner, stopka, „Wyślij teraz", Serwis) — krótki cache w pamięci
// (1,5 s) unieważniany przy KAŻDYM zapisie do `photo_queue` sprowadza to do jednego odczytu.
const QUEUE_STATS_CACHE_MS = 1500;
let _queueStatsCache = null; // { at, value }
function invalidateQueueStats() { _queueStatsCache = null; }

// ─── Memory backing (Node + tests) ───────────────────────────────────────────

function createMemoryBacking() {
  const stores = {
    api_cache: new Map(),
    photo_queue: new Map(),       // keyPath=id — wewnętrznie też Map, id bierzemy z value.id
    building_configs: new Map(),
    lost_photos: new Map(),       // R2b-b: rejestr „Utracone"
    settings: new Map(),          // V3 B9: ustawienia per urządzenie
    upload_log: new Map(),        // V3 B6: rejestr prób wysyłki
  };
  const keyPathStores = new Set(['photo_queue', 'lost_photos', 'upload_log']); // stores z keyPath (klucz ekstraktowany z value)

  return {
    async get(storeName, key) {
      return stores[storeName].get(key);
    },
    async keys(storeName) {
      return [...stores[storeName].keys()];
    },
    async put(storeName, value, key) {
      let realKey = key;
      if (realKey === undefined) {
        if (keyPathStores.has(storeName)) realKey = value.id;
        else throw new Error(`Store "${storeName}" requires explicit key`);
      }
      stores[storeName].set(realKey, value);
      return realKey;
    },
    async delete(storeName, key) {
      stores[storeName].delete(key);
    },
    async getAll(storeName) {
      return [...stores[storeName].values()];
    },
    async count(storeName) {
      return stores[storeName].size;
    },
    async clear(storeName) {
      stores[storeName].clear();
    },
    _isMemory: true,
  };
}

// ─── IndexedDB backing (browser) ─────────────────────────────────────────────

function createIDBBacking() {
  let dbPromise = null;

  function open() {
    if (dbPromise) return dbPromise;
    dbPromise = new Promise((resolve, reject) => {
      const req = indexedDB.open(DB_NAME, DB_VERSION);
      req.onupgradeneeded = (e) => {
        const db = e.target.result;
        if (!db.objectStoreNames.contains('api_cache')) {
          db.createObjectStore('api_cache'); // key=path (string) przekazujemy explicite
        }
        if (!db.objectStoreNames.contains('photo_queue')) {
          db.createObjectStore('photo_queue', { keyPath: 'id' });
        }
        if (!db.objectStoreNames.contains('building_configs')) {
          db.createObjectStore('building_configs'); // key=addressId
        }
        // v2 (R2b-b): rejestr „Utracone" — działa też przy upgrade v1→v2
        // (contains-check = ten sam wzorzec co wyżej).
        if (!db.objectStoreNames.contains('lost_photos')) {
          db.createObjectStore('lost_photos', { keyPath: 'id' });
        }
        // v3 (V3 B-1): ustawienia per urządzenie + rejestr prób wysyłki. Ten sam
        // wzorzec contains-check → upgrade v1/v2→v3 dopisuje store'y, nie rusza
        // photo_queue (wpisy pending/failed techników przeżywają, B8).
        if (!db.objectStoreNames.contains('settings')) {
          db.createObjectStore('settings'); // key=string przekazujemy explicite
        }
        if (!db.objectStoreNames.contains('upload_log')) {
          db.createObjectStore('upload_log', { keyPath: 'id' });
        }
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
    return dbPromise;
  }

  async function withStore(storeName, mode, op) {
    const db = await open();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(storeName, mode);
      const store = tx.objectStore(storeName);
      const result = op(store);
      tx.oncomplete = () => resolve(result);
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error);
    });
  }

  function reqToPromise(req) {
    return new Promise((resolve, reject) => {
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
  }

  return {
    async get(storeName, key) {
      const db = await open();
      const tx = db.transaction(storeName, 'readonly');
      return reqToPromise(tx.objectStore(storeName).get(key));
    },
    async keys(storeName) {
      const db = await open();
      const tx = db.transaction(storeName, 'readonly');
      return reqToPromise(tx.objectStore(storeName).getAllKeys());
    },
    async put(storeName, value, key) {
      const db = await open();
      const tx = db.transaction(storeName, 'readwrite');
      const store = tx.objectStore(storeName);
      const req = key !== undefined ? store.put(value, key) : store.put(value);
      return reqToPromise(req);
    },
    async delete(storeName, key) {
      const db = await open();
      const tx = db.transaction(storeName, 'readwrite');
      return reqToPromise(tx.objectStore(storeName).delete(key));
    },
    async getAll(storeName) {
      const db = await open();
      const tx = db.transaction(storeName, 'readonly');
      return reqToPromise(tx.objectStore(storeName).getAll());
    },
    async count(storeName) {
      const db = await open();
      const tx = db.transaction(storeName, 'readonly');
      return reqToPromise(tx.objectStore(storeName).count());
    },
    async clear(storeName) {
      const db = await open();
      const tx = db.transaction(storeName, 'readwrite');
      return reqToPromise(tx.objectStore(storeName).clear());
    },
    _isMemory: false,
  };
}

// ─── Public API ──────────────────────────────────────────────────────────────

// api_cache

export async function getCachedResponse(path) {
  const b = getBacking();
  const entry = await b.get('api_cache', path);
  if (!entry) return null;
  if (entry.expiresAt && entry.expiresAt < Date.now()) return null;
  return entry.data;
}

/**
 * V3 B3: wpis cache RAZEM ze stanem świeżości. Po timeoucie GET-u (słaby zasięg)
 * pokazujemy „ostatni stan" nawet po TTL — lepszy stary ekran niż czarny. null gdy brak.
 * @returns {Promise<{data:any, cachedAt:number, expiresAt:number, stale:boolean}|null>}
 */
export async function getCachedResponseEntry(path) {
  const b = getBacking();
  const entry = await b.get('api_cache', path);
  if (!entry) return null;
  const stale = !!(entry.expiresAt && entry.expiresAt < Date.now());
  return { data: entry.data, cachedAt: entry.cachedAt ?? null, expiresAt: entry.expiresAt ?? null, stale };
}

export async function setCachedResponse(path, data, ttlMs = DEFAULT_CACHE_TTL_MS) {
  const b = getBacking();
  const now = Date.now();
  await b.put('api_cache', { data, cachedAt: now, expiresAt: now + ttlMs }, path);
}

/**
 * V3 DN-D: unieważnia wpisy cache, których klucz (rola:ścieżka) zawiera podany
 * fragment ścieżki — np. `/api/config/recX` po zapisie konfiguracji albo zmianie typu WE.
 * Celowo nie czyści całego api_cache (lista adresów/tygodni zostaje dla trybu lokalnego).
 * @returns {Promise<number>} ile wpisów usunięto
 */
export async function invalidateCachedPrefix(pathFragment) {
  const b = getBacking();
  if (!pathFragment) return 0;
  const keys = await b.keys('api_cache');
  let n = 0;
  for (const k of keys) {
    if (typeof k === 'string' && k.includes(pathFragment)) {
      await b.delete('api_cache', k);
      n++;
    }
  }
  return n;
}

// photo_queue

function newId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  // Fallback dla Node bez crypto (rzadkie)
  return `q_${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

/**
 * R2b-a (monit 1215, 19.08, incydent D.Tyrek): treść zdjęcia trzymamy jako BAJTY
 * (ArrayBuffer) wewnątrz rekordu IDB, nie jako obiekt Blob. WebKit/iOS potrafi
 * zniszczyć ZAWARTOŚĆ Bloba przechowywanego w IndexedDB (metadane size/type
 * zostają, treści nie ma → „martwe bloby", 14×400 bez body 19.08). ArrayBuffer
 * jest częścią rekordu bazy i tej ścieżce nie podlega. Blob budowany dopiero
 * przy wysyłce (uploadManager.probeItem).
 *
 * Fallback: gdy odczyt treści się nie uda, zapis po staremu (Blob) — enqueue
 * NIGDY nie może zablokować zrobienia zdjęcia; taki wpis wykryje potem detektor.
 *
 * @returns {Promise<string>} id zakolejkowanego zdjęcia
 */
export async function addToPhotoQueue(photoData) {
  const b = getBacking();
  const id = photoData.id || newId();
  const src = photoData.blob;
  let content = { blob: src };
  if (src && typeof src.arrayBuffer === 'function') {
    try {
      const bytes = await src.arrayBuffer();
      content = {
        bytes,
        blobType: src.type || 'image/jpeg',
        blobSize: bytes.byteLength,
      };
    } catch {
      content = { blob: src }; // odczyt padł → stara ścieżka, detektor to złapie
    }
  }
  const entry = {
    id,
    // Plan posesji B2 (zasada twarda MONIT 1740: zmiana TYLKO w kluczu lokalizacji): sekcja jedzie
    // w addressId (`recXXX~n`); sekcja 0 ma jedną pisownię (`recXXX~0` → `recXXX`), więc stare
    // wpisy i nowe wpisy sekcji 0 są tym samym kluczem.
    addressId: queueAddressId(photoData.addressId),
    floor: photoData.floor,
    subfolder: photoData.subfolder,
    slotType: photoData.slotType,
    ...content,
    fileName: photoData.fileName || `photo_${Date.now()}.jpg`,
    createdAt: Date.now(),
    retryCount: 0,
    lastRetryAt: null,
    status: 'pending',
  };
  // V3 B-1: miniatura ⏳ (B12e) i metadane downscale (B6) — opcjonalne, wpisy bez
  // nich (sprzed v72) są w pełni obsługiwane.
  if (photoData.thumbBytes) entry.thumbBytes = photoData.thumbBytes;
  if (photoData.meta && typeof photoData.meta === 'object') entry.meta = photoData.meta;
  // E7a: zdjęcie zrobione przez małego admina w trybie AK jedzie z nagłówkiem trybu także po przełączeniu na MO.
  if (photoData.viewerAk === true) entry.viewerAk = true;
  await b.put('photo_queue', entry);
  invalidateQueueStats();
  return id;
}

export async function getPhotoQueue() {
  const b = getBacking();
  return b.getAll('photo_queue');
}

/** V3 B12e: jeden wpis kolejki (podgląd pełnego zdjęcia z telefonu w galerii). */
export async function getQueueItem(id) {
  const b = getBacking();
  return (await b.get('photo_queue', id)) || null;
}

export async function updateQueueItem(id, updates) {
  const b = getBacking();
  const existing = await b.get('photo_queue', id);
  if (!existing) return null;
  const merged = { ...existing, ...updates };
  await b.put('photo_queue', merged);
  invalidateQueueStats();
  return merged;
}

export async function removeFromQueue(id) {
  const b = getBacking();
  await b.delete('photo_queue', id);
  invalidateQueueStats();
}

export async function getQueueCount() {
  const b = getBacking();
  return b.count('photo_queue');
}

/**
 * FAZA F-7 (2026-06-12): rozdzielone liczniki dla banera — „X czeka" (pending +
 * uploading) vs „Y nieudanych" (failed, po MAX_RETRIES — reanimuje je przycisk
 * „Wyślij teraz" / trigger force). Wcześniej baner liczył wszystko jako „czeka",
 * co maskowało porzucone itemy (kolejka 15 u [D-F] wyglądała jak „czeka" a była martwa).
 */
export async function getQueueStats() {
  if (_queueStatsCache && Date.now() - _queueStatsCache.at < QUEUE_STATS_CACHE_MS) {
    return { ..._queueStatsCache.value };
  }
  const b = getBacking();
  const all = await b.getAll('photo_queue');
  // V3 B12f: `bytes` = suma rozmiarów treści (ostrzeżenie w banerze > 500 MB).
  // UZUPEŁNIENIE 2 (12.09): `oldestWaitingAt` = createdAt najstarszego wpisu czekającego
  // (pending/uploading) — punkt odniesienia zatoru, gdy nie było jeszcze żadnego sukcesu.
  const stats = { total: all.length, pending: 0, uploading: 0, failed: 0, bytes: 0, oldestWaitingAt: null };
  for (const it of all) {
    if (it.status === 'failed') stats.failed++;
    else {
      if (it.status === 'uploading') stats.uploading++;
      else stats.pending++;
      const c = Number(it.createdAt) || 0;
      if (c > 0 && (stats.oldestWaitingAt == null || c < stats.oldestWaitingAt)) stats.oldestWaitingAt = c;
    }
    stats.bytes += Number(it.blobSize ?? it.bytes?.byteLength ?? it.blob?.size ?? 0) || 0;
  }
  _queueStatsCache = { at: Date.now(), value: { ...stats } };
  return stats;
}

// settings — ustawienia per urządzenie (V3 B9: Tryb lokalny)

export async function getSetting(key) {
  const b = getBacking();
  const v = await b.get('settings', key);
  return v === undefined ? null : v;
}

export async function setSetting(key, value) {
  const b = getBacking();
  await b.put('settings', value, key);
}

// upload_log — rejestr prób wysyłki (V3 B6): diagnostyka „żeby następny raz nie zgadywać"

let _logSeq = 0;

/**
 * Dopisuje wpis rejestru prób wysyłki; ring ~UPLOAD_LOG_MAX (najstarsze wypadają).
 * Zero binariów. Zwraca id wpisu.
 */
export async function addUploadLogEntry(entry) {
  const b = getBacking();
  const at = entry?.at ?? Date.now();
  const id = `${String(at).padStart(13, '0')}_${String(++_logSeq).padStart(4, '0')}`;
  await b.put('upload_log', { ...entry, id, at });
  try {
    const n = await b.count('upload_log');
    if (n > UPLOAD_LOG_MAX + 50) {
      const all = await b.getAll('upload_log');
      all.sort((x, y) => (x.id < y.id ? -1 : x.id > y.id ? 1 : 0));
      for (const old of all.slice(0, n - UPLOAD_LOG_MAX)) await b.delete('upload_log', old.id);
    }
  } catch { /* ring best-effort */ }
  return id;
}

/** Ostatnie `limit` wpisów, najnowsze pierwsze. */
export async function getUploadLog(limit = 50) {
  const b = getBacking();
  const all = await b.getAll('upload_log');
  all.sort((x, y) => (x.id < y.id ? 1 : x.id > y.id ? -1 : 0));
  return all.slice(0, Math.max(0, limit));
}

// lost_photos — rejestr „Utracone" (R2b-b, decyzja Martina 19.08)

/**
 * Dopisuje ślad po wpisie kolejki, którego treść zniszczył system (martwy blob).
 * Zero binariów — wyłącznie metadane do pokazania technikowi (ekran Serwis)
 * i dołączenia do diagnostyki. Zero cichego znikania bez śladu.
 */
export async function addToLostRegistry(meta) {
  const b = getBacking();
  const entry = {
    id: meta.id || newId(),
    addressId: meta.addressId ?? null,
    floor: meta.floor ?? null,
    subfolder: meta.subfolder ?? null,
    slotType: meta.slotType ?? null,
    fileName: meta.fileName ?? null,
    declaredSize: meta.declaredSize ?? null,
    createdAt: meta.createdAt ?? null,        // kiedy zdjęcie było zrobione/zakolejkowane
    lostAt: meta.lostAt ?? Date.now(),        // kiedy wykryliśmy utratę
    reason: meta.reason || 'treść utracona przez system',
    // V3 B12a: odrzucenie w tle (duplikat) niesie lokalizację istniejącego zdjęcia
    // (znacznik w slocie, Serwis, diagnostyka).
    duplicate: meta.duplicate === true,
    location: meta.location && typeof meta.location === 'object' ? meta.location : null,
  };
  await b.put('lost_photos', entry);
  return entry.id;
}

export async function getLostRegistry() {
  const b = getBacking();
  const all = await b.getAll('lost_photos');
  return all.sort((a, z) => (z.lostAt || 0) - (a.lostAt || 0));
}

// building_configs

export async function getCachedConfig(addressId) {
  const b = getBacking();
  return (await b.get('building_configs', addressId)) || null;
}

export async function setCachedConfig(addressId, config) {
  const b = getBacking();
  await b.put('building_configs', { ...config, savedAt: Date.now() }, addressId);
}

// ─── Helpers (opcjonalne) ────────────────────────────────────────────────────

export async function clearAll() {
  const b = getBacking();
  await b.clear('api_cache');
  await b.clear('photo_queue');
  invalidateQueueStats();
  await b.clear('building_configs');
  await b.clear('lost_photos');
  await b.clear('upload_log');
  // `settings` celowo NIE: Tryb lokalny (B9) przeżywa restart i wylogowanie (Martin 12.09).
}

/**
 * B3.5.9 hotfix-3 (30.04 ~10:30 UTC): czyści tylko `api_cache` store.
 * Używane po operacjach które unieważniają stan adresu (np. reset / "Wyczyść
 * wszystko") — żeby AdminTaskListScreen przy następnym mount refetch'ował
 * fresh dane z backendu (NIE stale photoProgress z TTL 30 min).
 *
 * Bez tego: po reset adresu, ramka adresu w liście pozostaje pomarańczowa
 * (in_progress) z cache, mimo że backend zwraca empty. Cache TTL expire dopiero
 * po 30 min lub re-login.
 *
 * Czysty side-effect — `photo_queue` i `building_configs` zostają nietknięte.
 *
 * @returns {Promise<void>}
 */
export async function clearApiCache() {
  const b = getBacking();
  await b.clear('api_cache');
}
